import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

/**
 * Renders GitHub-flavored markdown (headings, lists, tables, code, links) as
 * on-brand HTML. Used for agent-authored prose like Athena's impact analysis,
 * which is emitted as markdown and otherwise shows raw.
 */
const components: Components = {
  h1: ({ children }) => <h1 className="text-lg font-bold text-gray-900 mt-4 mb-2 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold text-gray-900 mt-4 mb-2 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-gray-800 mt-3 mb-1.5 first:mt-0">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold text-gray-700 mt-2 mb-1">{children}</h4>,
  p: ({ children }) => <p className="text-sm text-gray-700 leading-relaxed mb-2.5">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 text-sm text-gray-700 mb-2.5 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 text-sm text-gray-700 mb-2.5 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noreferrer" className="text-primary underline hover:no-underline">
      {children}
    </a>
  ),
  code: ({ className, children }) => {
    const isBlock = /language-/.test(className || '');
    return isBlock ? (
      <code className={className}>{children}</code>
    ) : (
      <code className="bg-gray-100 text-gray-800 rounded px-1 py-0.5 text-[0.8em] font-mono">{children}</code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-gray-900 text-gray-100 rounded-lg p-3 overflow-x-auto text-xs mb-3 font-mono">{children}</pre>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto mb-3">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-left font-semibold text-gray-700 align-top">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-gray-200 px-2.5 py-1.5 text-gray-700 align-top">{children}</td>
  ),
  hr: () => <hr className="my-4 border-gray-200" />,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-gray-300 pl-3 text-gray-600 italic mb-2.5">{children}</blockquote>
  ),
};

export function Markdown({ children, className = '' }: { children: string; className?: string }) {
  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
